import { Component, OnInit, HostBinding, ViewChild, Injector, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { SharedService } from '../services/shared.service';
import { HomeService } from '../services/home.service';
import { Constants } from '../constants';
import { NewsComponent } from '../shared/news/news.component';
import { QrCodeComponent } from '../shared/qr-code/qr-code.component';
import { PlaylistComponent } from '../shared/playlist/playlist.component';
import { BackgroundComponent } from '../shared/background/background.component';
import { ModuleNameEnum, Piwiktrack } from '../shared/piwiktrack';
import { AppComponent } from '../app.component';
import { AuthService } from '../services/auth.service';
import { Subject, Subscription, takeUntil, filter, take, switchMap } from 'rxjs';
import { mvnfbuAnimations } from '../shared/animations';
import { Router } from '@angular/router';
import { AllNewsResult, VeNewsFeed, PlayList } from '../typings';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false,
  animations: mvnfbuAnimations
})
export class HomeComponent implements OnInit, OnDestroy {
  @HostBinding('class') class = 'fullWith';

  @ViewChild(NewsComponent) newsDisplayed: NewsComponent;
  @ViewChild(QrCodeComponent) qrCode: QrCodeComponent;
  @ViewChild(PlaylistComponent) playlist: PlaylistComponent;
  @ViewChild(BackgroundComponent) background: BackgroundComponent;

  private videoEndedSubscription?: Subscription;

  public blobUrl = '';
  public news: AllNewsResult;
  public currentNews: VeNewsFeed|null = null;
  public currentNewsIndex = 0;
  public animationStateIn = false;
  public animationStateOut = false;

  private appComponent: AppComponent;
  private lastTap = 0;
  private doubleTapDelay = 300; // milliseconds
  private newsChangeId?: number;
  private readonly timeoutIds = new Set<number>(); // Track all timeouts

  private destroy$ = new Subject<void>();
  private reloadTrigger$ = new Subject<void>();
  private prefetchedNews: AllNewsResult | null = null;
  private prefetchSubscription?: Subscription;
  
  constructor(private sharedService: SharedService, private homeService: HomeService, private injector: Injector, private router: Router, private authService: AuthService) {
    this.blobUrl = sharedService.blobUrl || '';
    this.appComponent = this.injector.get(AppComponent);
  }

  
  ngOnDestroy(): void {
    console.log("ngOnDestroy HomeComponent");
    this.destroy$.next();
    this.destroy$.complete();
    this.reloadTrigger$.complete();
    this.prefetchSubscription?.unsubscribe();
    this.clearAllTimeouts();
  }

  ngOnInit() {
    if( !this.sharedService.getToken())
      {
        this.sharedService.navigate([Constants.RedirectUrl.Auth]);
      }
    setTimeout(() => {
    this.initializeData();
    });
  }

  public onTouchStart(event: TouchEvent): void {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - this.lastTap;
    
    if (tapLength < this.doubleTapDelay && tapLength > 0) {
      event.preventDefault();
      this.handleDoubleTap();
    }
    
    this.lastTap = currentTime;
  }

  public onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
  }

  private handleDoubleTap(): void {
    if (this.playlist) {
      this.unsubscribeFromVideoEnded();
      if(this.sharedService.isVideo(this.playlist.currentPlayList.currentNews))
      {
        this.sharedService.emitVideoEnded();
      }
      this.handleShiftNews();
      this.videoEndedSubscription = this.sharedService.videoEnded$.subscribe(
        video => {
          this.handleShiftNews()
      });
    }
  }

  public initializeData() {
    this.sharedService.videoEnded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleShiftNews());

    // Rechargements : switchMap garantit qu'un rechargement en cours est annulé
    // si un nouveau est déclenché (double-tap, reconnexion, etc.)
    this.reloadTrigger$.pipe(
      switchMap(() => this.homeService.reloadNewsData()),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (elt) => this.applyReloadedNews(elt),
      error: (e: any) => {
        console.error(e);
        this.sharedService.navigate([Constants.RedirectUrl.Error]);
      }
    });

    const needsReload = this.homeService.getNeedToReloadNews();
    this.homeService.setNeedToReloadNews(false);

    // allNews est un BehaviorSubject : si des données sont déjà en cache, take(1) recevrait
    // immédiatement l'ancienne valeur avant la fin du loadNews() asynchrone. On passe donc
    // par reloadTrigger$ (Observable frais, sans valeur rejouée) pour un rechargement forcé.
    if (needsReload && this.homeService.allNews.getValue() !== null) {
      this.reloadTrigger$.next();
    } else {
      if (this.homeService.allNews.getValue() === null) {
        this.homeService.loadNews();
      }

      // Abonnement unique (take(1)) : cas du chargement initial, allNews n'a pas encore de valeur.
      this.homeService.allNews.pipe(
        filter((elt): elt is AllNewsResult => elt !== null),
        take(1),
        takeUntil(this.destroy$)
      ).subscribe({
        next: (elt) => {
          this.news = elt;
          if (this.news?.internalNews?.length > 0) {
            this.startFromIndex(this.homeService.getNewsIndex());
          } else {
            this.sharedService.navigate([Constants.RedirectUrl.Error, 'noNews']);
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error(error);
          this.sharedService.navigate([error.status === 401 ? Constants.RedirectUrl.Auth : Constants.RedirectUrl.Error]);
        }
      });
    }

    this.sharedService.getToken().then(token => {
      Piwiktrack.piwikTrackPage(this.homeService.uuid, ModuleNameEnum.ScreenView, token);
    });
  }


  // Méthode unifiée de démarrage à un index donné.
  // Utilisée à l'initialisation (index courant) et après rechargement (index 0).
  private startFromIndex(index: number): void {
    if (!this.news?.internalNews?.length) return;
    this.homeService.setNewsIndex(index);
    this.currentNewsIndex = index;
    this.currentNews = this.news.internalNews[index];
    this.newsDisplayed.setInitialValue(this.currentNews);
    this.background.setInitialValue(this.currentNews.image?.[0] || '');
    this.qrCode.setInitialValue(this.currentNews);
    const playList = this.createPlayList(this.currentNews);
    this.playlist.setInitialValue(playList);
    if (!this.sharedService.isVideo(this.currentNews)) {
      this.initializeTimerForNextNews();
    }
    this.tryPrefetchForLastNews();
  }

  // Applique les données rechargées (prefetch ou fallback via reloadTrigger$).
  private applyReloadedNews(elt: AllNewsResult): void {
    this.news = elt;
    if (this.news?.internalNews?.length > 0) {
      this.startFromIndex(0);
    } else {
      this.sharedService.navigate([Constants.RedirectUrl.Error, 'noNews']);
    }
  }

  // Lance un rechargement en arrière-plan dès que la dernière news commence à jouer.
  // Le résultat est mis en cache dans prefetchedNews pour être appliqué instantanément
  // à la fin de cette news, rendant la transition invisible.
  private tryPrefetchForLastNews(): void {
    const total = this.news?.internalNews?.length ?? 0;
    if (total === 0 || this.currentNewsIndex !== total - 1) return;

    this.prefetchedNews = null;
    this.prefetchSubscription?.unsubscribe();
    this.prefetchSubscription = this.homeService.reloadNewsData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => { this.prefetchedNews = data; },
        error: () => { this.prefetchedNews = null; } // fallback : reloadTrigger$ prendra le relais
      });
  }

  public clearNextNewsTimeout(): void {
    if (this.newsChangeId) {
      window.clearTimeout(this.newsChangeId);
      this.newsChangeId = undefined;
    }
    // Clear all tracked timeouts (should not be happening except if there is error in js or to quick doubletap)
    this.timeoutIds.forEach(id => window.clearTimeout(id));
    this.timeoutIds.clear();
  }


  // Méthode utilitaire pour calculer l'index précédent
  private getPreviousNewsIndex(): number {
    return this.currentNewsIndex - 1 < 0 ? this.news.internalNews?.length - 1 : this.currentNewsIndex - 1;
  }

  private createPlayList(currentNews: VeNewsFeed, previousNews?: VeNewsFeed): PlayList {
    return {
      previousNews: previousNews || this.news.internalNews[this.getPreviousNewsIndex()],
      currentNews: currentNews,
      nextNews1: this.news.internalNews[(this.currentNewsIndex + 1) % this.news.internalNews?.length],
      nextNews2: this.news.internalNews[(this.currentNewsIndex + 2) % this.news.internalNews?.length],
      nextNews3: this.news.internalNews[(this.currentNewsIndex + 3) % this.news.internalNews?.length],
      nextNews4: this.news.internalNews[(this.currentNewsIndex + 4) % this.news.internalNews?.length],
      nextNews5: this.news.internalNews[(this.currentNewsIndex + 5) % this.news.internalNews?.length]
    };
  }

  private clearAllTimeouts(): void {
      this.clearNextNewsTimeout();        
      try {
          this.newsDisplayed?.clearTimeouts?.();
          this.playlist?.clearChangeValueTimeout?.();
          this.playlist?.clearVideoTimeout?.();
      } catch (error) {
          console.warn('Error clearing child component timeouts:', error);
      }
  }

  private moveToNextNews(): void {
    this.clearNextNewsTimeout();
    // Vérifie la présence des news
    if (!this.news?.internalNews?.length) {
      console.warn('Aucune news disponible');
      return;
    }

    const previous = Object.assign({}, this.currentNews);

    // Calcule le nouvel index
    this.updateNewsIndex();
    this.tryPrefetchForLastNews();
    if(this.currentNews){
      // Cas particulier : news locale
      if (this.currentNews.id === '0') {
        if(this.sharedService.isVideo(previous)) {
          window.setTimeout(() => {
            this.handleLocalNews();
          }, 515); // attend la fin de l'animation
          return;
        }
        else{
          this.handleLocalNews();
          return;
        }
      }
      // Met à jour les composants enfants
      this.updateUIComponents(this.currentNews);

      // Force la réapparition de la playlist après une vidéo
      this.playlist.animationPlaylistStateOut = false;

      // Met à jour la playlist
      const playList = this.createPlayList(this.currentNews, previous);
      this.playlist.changeValue(playList);

      // Lance le timer si ce n'est pas une vidéo
      if (!this.sharedService.isStreamVideo(this.currentNews) && !this.sharedService.isYoutubeVideo(this.currentNews)) {
        this.initializeTimerForNextNews();
      }
    }
  }

// Méthode utilitaire pour l’index
private updateNewsIndex(): void {
  this.homeService.setNewsIndex((this.currentNewsIndex + 1) % this.news.internalNews?.length);
  this.currentNewsIndex = this.homeService.getNewsIndex();
  this.currentNews = this.news.internalNews[this.currentNewsIndex];
}

// Méthode utilitaire pour la gestion locale
private handleLocalNews(): void {
  this.clearAllTimeouts();
  this.homeService.setNewsIndex((this.currentNewsIndex + 1) % this.news.internalNews?.length);
  if (this.homeService.getNewsIndex() === 0) {
    this.homeService.setNeedToReloadNews(true);
  }
  this.currentNewsIndex = this.homeService.getNewsIndex();
  this.currentNews = this.news.internalNews[this.currentNewsIndex];
  this.sharedService.navigateByDisplayMode('local');
}

// Méthode utilitaire pour la mise à jour des composants
private updateUIComponents(news: VeNewsFeed): void {
  this.newsDisplayed.changeValue(news);
  this.background.changeValue(news.image?.[0] ?? '');
  this.qrCode.changeValue(news);
}

  private reloadNews(): void {
    // Redirigé vers reloadTrigger$ - conservé pour compatibilité éventuelle
    this.reloadTrigger$.next();
  }

  private initializeTimerForNextNews(): void {
    this.animationStateIn = true;
    this.clearNextNewsTimeout();
    this.newsChangeId = window.setTimeout(() => {
      this.handleShiftNews()
    }, (32500));

    if (this.newsChangeId) {
          this.timeoutIds.add(this.newsChangeId);
    }
  }

  private handleShiftNews(): void {
    this.clearNextNewsTimeout();
    this.animationStateIn = false;

    // Détecte si on termine la dernière news de la playlist.
    // Le flag est évalué ICI (après la fin effective) et non pas en début de news,
    // ce qui évite l'armement prématuré pendant une vidéo longue.
    const isLastNews = (this.news?.internalNews?.length ?? 0) > 0
      && this.currentNewsIndex === this.news.internalNews.length - 1;

    if (isLastNews) {
      if (this.prefetchedNews) {
        // Prefetch terminé : application instantanée, transition invisible.
        const data = this.prefetchedNews;
        this.prefetchedNews = null;
        this.prefetchSubscription?.unsubscribe();
        this.applyReloadedNews(data);
      } else {
        // Prefetch encore en cours ou en erreur : fallback sur le rechargement classique.
        this.prefetchSubscription?.unsubscribe();
        this.reloadTrigger$.next();
      }
    } else {
      this.moveToNextNews();
    }
  }

  private unsubscribeFromVideoEnded(): void {
    if (this.videoEndedSubscription) {
      this.videoEndedSubscription.unsubscribe();
      this.videoEndedSubscription = undefined;
    }
  }
}